import AddIcon from '@mui/icons-material/Add';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { FilteredProduct } from '@/api/filteredProducts';
import DeviceProductSelection from '@/components/PatientPageV2/components/DeviceSelector/DeviceSelector';
import type { VaporizerProductSelection } from '@/components/PatientPageV2/components/DeviceSelector/DeviceSelector.types';
import { FF_PAT_ENABLE_DEVICE_PRESCRIPTION } from '@/constants/featureFlags';
import { useFeatureFlags, useGetFilteredProducts } from '@/hooks';
import type { DeviceProduct } from '@/hooks/prescription/usePrescriptionForm';
import { useAppStore } from '@/state-management';

import { TutorialVideoModal } from '../CreatePrescriptionTutorialModal/TutorialVideoModal';
import { DispenseLimitExceededAlert } from '../DispenseLimitExceededNotification/DispenseLimitExceededAlert';
import { MedicationPadItemEditor } from '../PrescriptionItemEditor/MedicationPadItemEditor';
import { PrescriptionPadItemDetailsModal } from '../PrescriptionPadItemDetailsModal/PrescriptionPadItemDetailsModal';

export interface PrescriptionPadProps {
  doctorId: number;
  isConcessionCardHolder?: boolean;
}

/**
 * Component used for creating medications.
 */
export const PrescriptionPad = ({ doctorId, isConcessionCardHolder }: PrescriptionPadProps) => {
  const { flags } = useFeatureFlags();
  const ffEnableDevicePrescription = flags[FF_PAT_ENABLE_DEVICE_PRESCRIPTION];
  const {
    prescriptionPad: { medicationPadItems, devicePadItems },
  } = useAppStore.use.prescriptionAssistant();
  const updateDevicePadItems = useAppStore.use.updateDevicePadItems();

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [isTutorialVideoOpen, setIsTutorialVideoOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [deviceEntriesList, setDeviceEntriesList] = useState<[string, DeviceProduct | null][]>(() =>
    (devicePadItems || []).map((deviceItem) => {
      const newListId = uuidv4();
      return [newListId, deviceItem];
    }),
  );
  const { data: filteredProductsData, isLoading: isFilteredProductsLoading } = useGetFilteredProducts({
    doctorId,
    isConcession: isConcessionCardHolder,
  });

  useEffect(() => {
    const deviceProducts: DeviceProduct[] = deviceEntriesList
      .map(([, device]) => device)
      .filter((item) => !!item) as DeviceProduct[];
    updateDevicePadItems(deviceProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceEntriesList]);

  const handleDeleteDeviceFromList = useCallback((listIdToDelete: string) => {
    setDeviceEntriesList((prevList) => {
      const newList = new Map(prevList);
      newList.delete(listIdToDelete);
      return Array.from(newList);
    });
  }, []);

  const handleSetDeviceAtListId = useCallback(
    (listIdToUpdate: string, { product, quantity }: VaporizerProductSelection) => {
      const newList = new Map(deviceEntriesList);
      const targetedDevice = newList.get(listIdToUpdate);
      // only update if we need to
      if (targetedDevice?.product_id === product.id) {
        return;
      }

      setDeviceEntriesList(() => {
        newList.set(listIdToUpdate, {
          product_id: product.id,
          name: product.productName,
          quantity,
          vaporizer: true,
        });
        return Array.from(newList);
      });
    },
    [deviceEntriesList],
  );

  const handleAddDevice = () => {
    setDeviceEntriesList((prevList) => {
      const newListId = uuidv4();
      return [...prevList, [newListId, null]];
    });
  };

  const handlePreviewIconClick = (productToPreview: FilteredProduct | null) => {
    setDetailsModalOpen(true);
    if (productToPreview?.id) {
      setSelectedProductId(productToPreview.id);
    }
  };

  const renderedDevices = useMemo(
    () =>
      deviceEntriesList.map(([listId, device], index) => {
        return (
          <DeviceProductSelection
            key={listId}
            ariaLabel={`device selection ${index + 1}`}
            doctorId={doctorId}
            initialSelectedDevice={device}
            onSelect={(selected) => handleSetDeviceAtListId(listId, selected)}
            onDelete={() => handleDeleteDeviceFromList(listId)}
            onPreviewIconClick={(item) => handlePreviewIconClick(item)}
          />
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deviceEntriesList, doctorId],
  );

  const renderedPrescriptionItems = useMemo(() => {
    return (
      filteredProductsData &&
      (medicationPadItems || []).map((medication) => {
        return (
          <MedicationPadItemEditor
            key={medication.medicationPadItemId}
            doctorId={doctorId}
            medicationItemId={medication.medicationPadItemId}
            filterDataSource={filteredProductsData.productFilters}
            medicationDataSource={filteredProductsData.products}
            onPreviewIconClick={handlePreviewIconClick}
          />
        );
      })
    );
  }, [medicationPadItems, filteredProductsData, doctorId]);

  if (isFilteredProductsLoading) {
    return (
      <Stack direction="row" alignItems="center" justifyContent="center" height={300}>
        <CircularProgress color="inherit" />
      </Stack>
    );
  }

  return (
    <>
      {/* Prescription Details Modal */}
      {selectedProductId && (
        <PrescriptionPadItemDetailsModal
          productId={selectedProductId}
          isModalOpen={detailsModalOpen}
          isConcessionCardHolder={isConcessionCardHolder}
          onClose={() => setDetailsModalOpen(false)}
        />
      )}
      <TutorialVideoModal open={isTutorialVideoOpen} handleClose={() => setIsTutorialVideoOpen(false)} />
      <Stack spacing={6} py={2}>
        {' '}
        {renderedPrescriptionItems}
      </Stack>

      <DispenseLimitExceededAlert />

      {ffEnableDevicePrescription && (
        <Stack spacing={4}>
          <Button
            variant="text"
            sx={{
              minWidth: 6,
              textTransform: 'capitalize',
              justifyContent: 'flex-start',
              width: 'fit-content',
              fontWeight: 'bold',
            }}
            color={'warning'}
            onClick={handleAddDevice}
            endIcon={<AddIcon />}
          >
            <Typography fontWeight="unset">Add device</Typography>
          </Button>
          {renderedDevices}
        </Stack>
      )}
    </>
  );
};
